/**
 * Video Upload Service
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 28/02/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author              | Task                 | Comments |
 * 1      13/03/2025    Nidhin Sudhakaran     | Creating new File    |
 */

import api from "./api.ts"; 
import axios from "axios";
import Constant from "../global/Constant.ts";

class VideoUploadService {
  /**
   * Sanitize file name by removing special characters
   * @param originalName - Original file name
   * @returns Sanitized file name
   */
  sanitizeFileName(originalName: string): string {
    return originalName.replace(/[^a-zA-Z0-9.]/g, "");
  }
  

  /**
   * Get a pre-signed URL for uploading a file to S3.
   * @param fileName - Name of the file
   * @param fileType - MIME type of the file
   * @param AssetID - ID of the asset
   * @returns Pre-signed URL response
   */
  async getPresignedUrl(fileName: string, fileType: string, AssetID: number) {
    try {
      const sanitizedFileName = this.sanitizeFileName(fileName);
      const response = await api.post(Constant.s3VideoUploadUrl, {
        fileName: sanitizedFileName,
        fileType,
        AssetID,
      });

      if (response.status === 200 && response.data.success) {
        return response.data;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error getting pre-signed URL:", error);
      throw error;
    }
  }

  /**
   * Upload file to S3 using the pre-signed URL.
   * @param file - File object to be uploaded
   * @param uploadUrl - Pre-signed URL obtained from backend
   * @returns Success status
   */
  async uploadFileToS3(file: File, uploadUrl: string) {
    try {
      const response = await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      if (response.status === 200) {
        return { success: true };
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error uploading file to S3:", error);
      throw error;
    }
  }

  /**
   * Get a pre-signed URL for streaming a video from S3.
   * @param AssetID - ID of the asset
   * @param fileName - Name of the file
   * @returns Pre-signed streaming URL
   */
  async getVideoStreamUrl(AssetID: number, fileName: string) {
    try {
      const sanitizedFileName = this.sanitizeFileName(fileName);
      const url = `${Constant.getVideoUrl}?AssetID=${AssetID}&fileName=${encodeURIComponent(sanitizedFileName)}`;
      const response = await api.get(url);

      if (response.status === 200 && response.data.success) {
        return response.data.streamUrl;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error getting video stream URL:", error);
      throw error;
    }
  }

  /**
   * Get a pre-signed URL for uploading a thumbnail to S3.
   * @param fileName - Name of the thumbnail file
   * @param fileType - MIME type of the thumbnail file
   * @returns Pre-signed URL response
   */
  async getThumbnailPresignedUrl(AssetID: number,fileName: string, fileType: string) {
    try {
      const response = await api.post(Constant.s3VideoUploadUrl, {
        fileName,
        fileType,
        isThumbnail: true,
        AssetID,
      });
      if (response.status === 200 && response.data.success) {
        return response.data;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error getting thumbnail pre-signed URL:", error);
      throw error;
    }
  }
}

const videoUploadService = new VideoUploadService();
export default videoUploadService;
